import {
  AppointmentStatusCodes,
  ContactType,
  QueryFactory,
  SearchDirection,
  WaitingListPriority,
} from '@lmt/shared-types';

import { SchedulingAssistant } from '@lmt/unicorn-shared/app/components/calendar/assistant/SchedulingAssistant';
import {
  convertIntervalToFormField,
  findIntersectionsBetweenManyIntervalGroups,
} from '@lmt/unicorn-shared/app/components/calendar/visit/common/time-operations';
import FreeTimeSlotSearchControl from '@lmt/unicorn-shared/app/components/calendar/visit/components/FreeTimeSlotSearchControl';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { getDoctorFullName } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import {
  patientResourceType,
  useVisitStatusOptions,
  VisitCreatingExpandedFormProps,
} from '../common/types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import {
  addMilliseconds,
  addSeconds,
  areIntervalsOverlapping,
  Interval,
  max,
  min,
} from 'date-fns';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getContactTypeFromField,
  patientContactFields,
} from '@lmt/unicorn-shared/app/features/patients/types';
import clsx from 'clsx';
import { Field } from '../../../common/Field';
import {
  PatientSearch,
  SearchInputPatientData,
} from '../../../common/patient/PatientSearch';
import {
  DateTimeIntervalPicker,
  stringifyDateTimeIntervalForMemoization,
} from '../../../common/ui-kit/inputs/date-time/DateTimeIntervalPicker';
import {
  DropDownInput,
  HookFormDropDownInput,
} from '../../../common/ui-kit/inputs/dropdown/DropDownInput';
import { TagsEditor } from '@lmt/unicorn-shared/app/components/common/ui-kit/tags/TagsEditor';

import styles from './VisitCreatingExpandedForm.module.scss';
import { CaseData } from '@lmt/unicorn-shared/app/helpers/case-queries';
import { useEventCallback } from '@mui/material';

interface Contact {
  type: ContactType;
  value: string;
}

const defaultTimeStepInMinutes = 30;
const searchIntervalForwardInDays = 30;
const searchIntervalBackwardInDays = 30;

export const VisitCreatingExpandedForm: FC<VisitCreatingExpandedFormProps> = (
  props,
) => {
  const {
    visitTypes,
    form,
    cases,
    addNewPatient,
    addNewCase,
    onChangeCase,
    selectPatient,
    takenTimeSlots,
    takenTimeSlotsForResources,
    resourceSettings,
    resourceGroups,
    setResourceSelected,
    resourcesLoaded,
    selectedResources,
    isWeekViewByDefault,
    attendingDoctors,
    isAttendingDoctorsLoading,
    timeSlotTimeStepInMinutes,
    typesWithAnyAvailableResource,
    setTypesWithAnyAvailableResource,
  } = props;
  const i18n = useTranslation();
  const selectPatientInInput =
    useRef<(patient: SearchInputPatientData) => void>();

  const { formState, watch, control, setValue } = form;
  const [
    patient,
    dateTimeInterval,
    visitTypeId,
    resources,
    inWaitingList,
    priority,
    note,
  ] = watch([
    'patient',
    'dateTimeInterval',
    'visitTypeId',
    'resources',
    'waitingList',
    'priority',
    'note',
  ]);

  const [isWeekView, setIsWeekView] = useState<boolean>(
    isWeekViewByDefault ?? false,
  );

  const contacts: Contact[] = useMemo(() => {
    const result: Contact[] = [];
    if (patient) {
      patientContactFields.forEach((field) => {
        const value = patient[field];
        if (value) {
          result.push({ type: getContactTypeFromField(field), value });
        }
      });
    }
    return result;
  }, [patient]);

  const visitStatuses = useVisitStatusOptions();

  const startDateTime = useMemo(
    () =>
      dateTimeInterval.startDate &&
      dateTimeInterval.startTimeInMills !== undefined &&
      dateTimeInterval.startTimeInMills !== null
        ? addMilliseconds(
            dateTimeInterval.startDate,
            dateTimeInterval.startTimeInMills,
          )
        : undefined,
    [stringifyDateTimeIntervalForMemoization(dateTimeInterval)],
  );

  const endDateTime = useMemo(
    () =>
      dateTimeInterval.endDate &&
      dateTimeInterval.endTimeInMills !== undefined &&
      dateTimeInterval.endTimeInMills !== null
        ? addMilliseconds(
            dateTimeInterval.endDate,
            dateTimeInterval.endTimeInMills,
          )
        : undefined,
    [stringifyDateTimeIntervalForMemoization(dateTimeInterval)],
  );
  const selectedInterval: Interval | undefined = useMemo(() => {
    return (
      startDateTime &&
      endDateTime && {
        start: startDateTime,
        end: endDateTime,
      }
    );
  }, [startDateTime?.getTime(), endDateTime?.getTime()]);

  const onIntervalChanged = useEventCallback((newInterval: Interval) => {
    setValue(
      'dateTimeInterval',
      convertIntervalToFormField(newInterval),
      validateAndDirty,
    );
  });

  const isTimeSlotSearchAvailable: boolean = useMemo(() => {
    return !!(visitTypeId && patient);
  }, [visitTypeId, patient]);

  const timeStepInMinutes = useMemo(
    () => timeSlotTimeStepInMinutes ?? defaultTimeStepInMinutes,
    [timeSlotTimeStepInMinutes],
  );

  /**
   * Key is resource ID
   * Value is a list of occupied intervals for this resource
   */
  const flatResourcesWithTimeSlots: Record<number, Interval[]> = useMemo(() => {
    const result: Record<number, Interval[]> = {};
    Object.keys(takenTimeSlotsForResources).forEach((type) => {
      for (const resource of takenTimeSlotsForResources[type]) {
        result[resource.resourceId] = resource.slots;
      }
    });
    return result;
  }, [takenTimeSlotsForResources]);

  /**
   * Collection of all unavailable intervals (i.e. intervals where all resources are occupied) for each resource type
   */
  const unavailableTimeSlots = useMemo(() => {
    const result: Record<string, Interval[]> = {};
    Object.keys(takenTimeSlotsForResources).forEach((type) => {
      result[type] = findIntersectionsBetweenManyIntervalGroups(
        takenTimeSlotsForResources[type].map((x) => x.slots),
      );
    });
    return result;
  }, [takenTimeSlotsForResources]);

  const goToNextFreeTimeSlot = useEventCallback(
    async (
      searchDirection: SearchDirection,
      skipCurrentDay: boolean,
      isParticularTime: boolean,
    ) => {
      if (!isTimeSlotSearchAvailable) {
        return;
      }
      const timeIntervalInSeconds =
        startDateTime && endDateTime
          ? (endDateTime?.getTime() - startDateTime?.getTime()) / 1000
          : undefined;

      const selectedResourceIds = Object.keys(resources)
        .filter(
          (type) =>
            type !== patientResourceType &&
            !typesWithAnyAvailableResource.includes(type),
        )
        .flatMap((type) => resources[type])
        .map((resource) => resource.id);

      let foundTimeSlot =
        await QueryFactory.AppointmentEventsClient.findFreeTimeSlot(
          patient?.id,
          visitTypeId ?? undefined,
          startDateTime,
          timeIntervalInSeconds,
          timeStepInMinutes * 60,
          searchDirection,
          skipCurrentDay,
          isParticularTime,
          searchIntervalForwardInDays,
          searchIntervalBackwardInDays,
          props.visitId,
          selectedResourceIds,
        );

      if (typesWithAnyAvailableResource.length) {
        while (true) {
          const overlappingIntervals: Interval[] = [];
          // find intersection of `foundTimeSlot` with selected 'any available' resources (where all resources are occupied)
          for (const type of typesWithAnyAvailableResource) {
            overlappingIntervals.push(
              ...(unavailableTimeSlots[type]?.filter((slot) =>
                areIntervalsOverlapping(slot, foundTimeSlot),
              ) ?? []),
            );
          }

          // find intersection of `foundTimeSlot` with explicitly selected resources
          overlappingIntervals.push(
            ...selectedResourceIds
              .flatMap((resourceId) => flatResourcesWithTimeSlots[resourceId])
              .filter((slot) => areIntervalsOverlapping(slot, foundTimeSlot)),
          );

          if (overlappingIntervals.length === 0) {
            // if there's no overlapping - we have found a free slot!
            break;
          }

          // if there's an overlapping, we will try another interval starting with the end of overlapped intervals
          if (searchDirection === SearchDirection.Forward) {
            const newStart = max(overlappingIntervals.map((x) => x.end));
            foundTimeSlot = {
              start: newStart,
              end: addSeconds(newStart, timeIntervalInSeconds!),
            };
          } else {
            const newEnd = min(overlappingIntervals.map((x) => x.start));
            foundTimeSlot = {
              start: addSeconds(newEnd, -timeIntervalInSeconds!),
              end: newEnd,
            };
          }
        }
      }

      onIntervalChanged(foundTimeSlot);
    },
  );

  // set high priority as default if priority of visit is not set
  useEffect(
    function setHighPriorityAsDefault() {
      if (inWaitingList && priority) {
        return;
      }
      setValue('priority', inWaitingList ? WaitingListPriority.High : null);
    },
    [inWaitingList],
  );

  return (
    <>
      <div className={styles.topSection}>
        <Field
          title={i18n.t('event.visit.fields.patient')}
          required={true}
          className={styles.field}
          linkProps={{
            title: i18n.t('event.visit.add_new_patient'),
            onClick: () => addNewPatient(selectPatientInInput.current),
          }}
        >
          <Controller
            control={control}
            name={'patient'}
            render={({ field: { value } }) => (
              <PatientSearch
                className={styles.patientSearch}
                optionContainerClassName={styles.patientSearchOption}
                isOnlyActive={true}
                onSelectedPatientChanged={selectPatient}
                selectedPatient={value}
                errorText={(formState.errors.patient as any)?.message}
                disabled={props.visitId !== undefined}
                optionListWidth={300}
                variant={'formInput'}
              />
            )}
          />
        </Field>
        <Field
          title={i18n.t('event.visit.fields.case')}
          required={true}
          className={styles.field}
          linkProps={{
            title: i18n.t('event.visit.add_new_case'),
            disabled: !patient,
            onClick: () => addNewCase(patient?.id),
          }}
        >
          <Controller
            control={control}
            name={'caseId'}
            render={({ field: { onChange, value } }) => (
              <DropDownInput<CaseData, true, true>
                useIdFunctionAsValue={true}
                required={true}
                variant={'formInput'}
                options={cases}
                disabled={!patient}
                value={value}
                onValueChanged={(selectedCase) => onChangeCase(selectedCase.id)}
                idFunction={'id'}
                getOptionLabel={'title'}
                errorText={formState.errors.caseId?.message}
                getOptionDisabled={(x) => x.isClosed}
              />
            )}
          />
        </Field>
        <Field
          title={i18n.t('event.visit.fields.contacts')}
          className={styles.field}
        >
          <Controller
            control={control}
            name={'priorityContactType'}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                useIdFunctionAsValue={true}
                required={true}
                variant={'formInput'}
                options={contacts}
                value={value as any}
                disabled={!patient}
                onValueChanged={(selectedType) => onChange(selectedType.type)}
                idFunction={'type'}
                getOptionLabel={'value'}
                errorText={formState.errors.priorityContactType?.message}
              />
            )}
          />
        </Field>
        <Field
          title={i18n.t('event.visit.fields.visit_type')}
          className={styles.field}
          required={true}
        >
          <Controller
            control={control}
            name={'visitTypeId'}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                useIdFunctionAsValue={true}
                required={true}
                variant={'formInput'}
                options={visitTypes}
                value={value}
                onValueChanged={(selectedType) => onChange(selectedType.id)}
                idFunction={'id'}
                getOptionLabel={'label'}
                errorText={formState.errors.visitTypeId?.message}
              />
            )}
          />
        </Field>
        <Field
          title={i18n.t('event.visit.fields.attending_doctor')}
          className={styles.field}
        >
          <HookFormDropDownInput
            control={control}
            name={'attendingDoctorId'}
            options={attendingDoctors}
            enableSearch={true}
            useIdFunctionAsValue={true}
            useVirtualization={true}
            getOptionLabel={getDoctorFullName}
            idFunction="id"
            errorText={formState.errors.attendingDoctorId?.message}
            testId="patient-doctorId"
            loading={isAttendingDoctorsLoading}
          />
        </Field>
        {props.visitId && (
          <Field
            title={i18n.t('event.visit.fields.visit_status')}
            className={styles.field}
          >
            <Controller
              control={control}
              name={'statusId'}
              render={({ field: { onChange, value } }) => (
                <DropDownInput
                  useIdFunctionAsValue={true}
                  required={true}
                  variant={'formInput'}
                  options={visitStatuses.slice(1)}
                  value={value}
                  onValueChanged={(selectedStatus) => {
                    onChange(selectedStatus.value);
                    if (props.onStatusChange) {
                      props.onStatusChange(
                        selectedStatus.value ===
                          AppointmentStatusCodes.Canceled,
                      );
                    }
                  }}
                  idFunction={'value'}
                  getOptionLabel={'label'}
                  errorText={formState.errors.statusId?.message}
                />
              )}
            />
          </Field>
        )}
        <Field
          title={i18n.t('event.visit.fields.note')}
          className={clsx(styles.field, styles.notes)}
        >
          <TagsEditor
            value={note ?? ''}
            onChange={(e, value) =>
              setValue('note', value, { shouldDirty: true })
            }
            isMultiline={true}
          />
        </Field>
      </div>
      <div className="globalFormsContentSeparator" />
      <div className={styles.bottomSection}>
        <div className={styles.bottomTopPart}>
          <Controller
            control={control}
            name={'dateTimeInterval'}
            render={({ field: { onChange, value } }) => (
              <DateTimeIntervalPicker
                value={value}
                onIntervalChanged={onChange}
                datePickerClassName={styles.datePicker}
                startDateError={!!formState.errors.dateTimeInterval?.startDate}
                startDateErrorText={
                  (formState.errors.dateTimeInterval?.startDate as any)?.message
                }
                startTimeError={
                  !!formState.errors.dateTimeInterval?.startTimeInMills
                }
                startTimeErrorText={
                  formState.errors.dateTimeInterval?.startTimeInMills?.message
                }
                endDateError={!!formState.errors.dateTimeInterval?.endDate}
                endDateErrorText={
                  (formState.errors.dateTimeInterval?.endDate as any)?.message
                }
                endTimeError={
                  !!formState.errors.dateTimeInterval?.endTimeInMills
                }
                endTimeErrorText={
                  formState.errors.dateTimeInterval?.endTimeInMills?.message
                }
              />
            )}
          />
          {/*<Field*/}
          {/*  title={i18n.t('event.visit.alert_patient')}*/}
          {/*  className={styles.alertPatient}*/}
          {/*>*/}
          {/*  <Controller*/}
          {/*    control={control}*/}
          {/*    name={'alertPatient'}*/}
          {/*    render={({ field: { onChange, value } }) => (*/}
          {/*      <DropDownInput useIdFunctionAsValue={true}*/}
          {/*        required={true}*/}
          {/*        variant={'formInput'}*/}
          {/*        options={alertPatientOptions}*/}
          {/*        valueField={'intervalBeforeInMills'}*/}
          {/*        labelField={'label'}*/}
          {/*        value={value}*/}
          {/*        onSelectedOptionChanged={(option) =>*/}
          {/*          onChange(option.intervalBeforeInMills)*/}
          {/*        }*/}
          {/*      />*/}
          {/*    )}*/}
          {/*  />*/}
          {/*</Field>*/}
        </div>
        <div className="globalFormsContentSeparator" />
        <div className={styles.controlBar}>
          <CheckBox
            name={'isWeekView'}
            title={i18n.t('event.visit.week_view_checkbox')}
            onChange={(e) => {
              setIsWeekView(e.target.checked);
            }}
            checked={isWeekView}
          />
          <FreeTimeSlotSearchControl
            isSearchAvailable={isTimeSlotSearchAvailable}
            onSearch={goToNextFreeTimeSlot}
            timeStepInMinutes={timeStepInMinutes}
          />
        </div>
        <div className={styles.scheduler}>
          <Loading loading={!resourcesLoaded}>
            <SchedulingAssistant
              key={isWeekView.toString()}
              selectedInterval={selectedInterval}
              onIntervalChanged={onIntervalChanged}
              resourceSettings={resourceSettings}
              resourceGroups={resourceGroups}
              setResourceSelected={setResourceSelected}
              takenTimeSlots={takenTimeSlots}
              takenTimeSlotsForResources={takenTimeSlotsForResources}
              selectedResources={selectedResources}
              isWeekView={isWeekView}
              anyAvailableResourceEnabled={true}
              typesWithAnyAvailableResource={typesWithAnyAvailableResource}
              setTypesWithAnyAvailableResource={
                setTypesWithAnyAvailableResource
              }
              unavailableTimeSlots={unavailableTimeSlots}
              resourcesLoaded={resourcesLoaded}
            />
          </Loading>
        </div>
      </div>
    </>
  );
};
